import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AuthStore } from '@rapider/authentication';
import { ProjectControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { WORKSPACE_CONFIG, generateWorkspaceKey } from '../workspace.config';
// import { RapiderListGridComponent } from '@rapider/list-grid';

/**
 * @class WorkspaceCreateComponent
 * @description Handles the creation of a new workspace. Automates key generation and passes configuration to the API.
 */
@Component({
  selector: 'rapider-workspace-create',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './workspace-create.component.html'
})
export class WorkspaceCreateComponent {
  private readonly projectController = inject(ProjectControllerService);
  private readonly authStore = inject(AuthStore);
  private readonly notificationService = inject(NotificationService);

  public readonly config = WORKSPACE_CONFIG;

  // Form Signals
  public workspaceName = signal<string>('');
  public workspaceKey = signal<string>('');
  public workspaceDescription = signal<string>('');

  // State Signals
  public isSubmitting = signal<boolean>(false);
  public isKeyManuallyEdited = signal<boolean>(false);

  /**
   * Handles changes to the Workspace Name input.
   * Auto-generates the key if the user hasn't manually edited the key field.
   * @param {string} name - The updated name value.
   */
  public onNameChange(name: string): void {
    this.workspaceName.set(name);
    if (!this.isKeyManuallyEdited()) {
      this.workspaceKey.set(generateWorkspaceKey(name));
    }
  }

  /**
   * Handles manual edits to the Workspace Key input.
   * @param {string} key - The manually updated key value.
   */
  public onKeyChange(key: string): void {
    this.workspaceKey.set(generateWorkspaceKey(key));
    this.isKeyManuallyEdited.set(true);
  }

  /**
   * Validates and submits the form data to create a new workspace project.
   */
  public createWorkspace(): void {
    const name = this.workspaceName().trim();
    const key = this.workspaceKey().trim() || generateWorkspaceKey(name);

    if (!name || !key) return;

    this.isSubmitting.set(true);

    this.projectController.create_v2({
      body: {
        name,
        key,
        description: this.workspaceDescription().trim(),
        appType: 'mobile',
        backendFramework: 'loopback4',
        frontendFramework: 'angular'
      }
    }).subscribe({
      next: (newProject) => {
        this.isSubmitting.set(false);
        this.notificationService.createNotification('success', 'Success', this.config.notifications.createSuccess);

        // Commit the newly created workspace as the active project
        this.authStore.setActiveWorkspace({ project: newProject });
      },
      error: () => {
        this.isSubmitting.set(false);
        this.notificationService.createNotification('error', 'Error', this.config.notifications.createFailed);
      }
    });
  }
}